/**
 * Katalog reklam. `ads/{adId}` to jedyny egzemplarz kreacji — aplikacje gościa
 * czytają go wprost, filtrując po `assigned_hotels`. Zmiana grafiki to jeden
 * zapis, niezależnie od liczby hoteli.
 *
 * Ruch z ACS idzie przez funkcje `v3-*` (codebase `admin`): reguły dają klientom
 * wyłącznie odczyt, bo `assigned_hotels` steruje widocznością i personel
 * pojedynczego hotelu nie może go ruszać. Backend pilnuje tego samego warunku,
 * co wejście do ACS (`verifyAdminAccess`).
 */
export const AD_TYPES = ['BANNER', 'ATTRACTION'] as const;
export type AdType = typeof AD_TYPES[number];

/** Etykiety typów — `BANNER` i `ATTRACTION` trafiają w inne miejsca w aplikacji. */
export const AD_TYPE_LABELS: Record<AdType, string> = {
  BANNER: 'Baner (strona główna)',
  ATTRACTION: 'Wśród atrakcji',
};

export interface Ad {
  id: string;
  /** Nazwa robocza — zostaje w katalogu, nie schodzi do hoteli. */
  name: string;
  image: string;
  type: AdType;
  order: number;
  url: string;
  assigned_hotels: string[];
  /** ms — Timestamp nie przechodzi czysto przez JSON; wpisy sprzed migracji go nie mają. */
  createdAt: number | null;
  updatedAt: number | null;
}

export interface AdRequest {
  name: string;
  image: string;
  type: AdType;
  order: number;
  url: string;
  assigned_hotels?: string[];
}

/**
 * Odpowiedzi mutacji oddają stan przypisań po zapisie — treść reklamy leży
 * w jednym dokumencie, więc raportować trzeba już tylko to, komu się wyświetla.
 */
export interface AdMutationResult {
  id: string;
  assigned_hotels?: string[];
  added?: string[];
  removed?: string[];
  /** Hotele odsiane przez backend, bo dokument hotelu nie istnieje. */
  skipped?: string[];
}

/** Wynik `v3-deleteAds` — single i bulk zwracają ten sam kształt. */
export interface DeleteAdsResult {
  deletedCount: number;
  totalRequested: number;
  notFound: string[];
}

/**
 * Rozbicie na aplikacje, którymi gość zobaczył reklamę. Klucze to wartości
 * parametru `client` wysyłanego przez klienty; `other` zbiera zdarzenia sprzed
 * rejestracji wymiaru w GA4, żeby suma zgadzała się z licznikiem ogólnym.
 */
export interface AdPlatformCounts {
  webApp: number;
  mobileIOS: number;
  mobileAndroid: number;
  stationary: number;
  other: number;
}

export const AD_PLATFORM_LABELS: Record<keyof AdPlatformCounts, string> = {
  webApp: 'Przeglądarka',
  mobileIOS: 'iOS',
  mobileAndroid: 'Android',
  stationary: 'POD',
  other: 'Nieprzypisane',
};

/**
 * Statystyki reklamy z GA4 (`v3-getAdAnalytics`). Liczby dojrzewają po stronie
 * Google do ok. doby, więc świeża reklama pokazuje zera mimo działającego
 * trackingu — to nie jest błąd panelu.
 */
export interface AdAnalytics {
  adId: string;
  /** Hotel, do którego zawężono raport; `null` = suma po wszystkich. */
  hotelId: string | null;
  period: { startDate: string; endDate: string; days: number };
  totals: { impressions: number; clicks: number; ctr: number };
  /** Daty w formacie `DD-MM-YYYY`, z zerami w dniach bez ruchu. */
  daily: Array<{ date: string; impressions: number; clicks: number }>;
  byPlatform: { impressions: AdPlatformCounts; clicks: AdPlatformCounts };
  byHour: Array<{ hour: number; impressions: number }>;
}

/** Koperta `responseHandler` z backendu — mutacje sięgają po samo `data`. */
export interface AdsApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}
